/**
 * A grid for attaching tracking metadata (item name, revision, assembly) to
 * each part before export. Same hand-built-overlay approach as
 * AuthDialog/ProfileDialog — see authDialog.ts's comment on why a native
 * `<dialog>` is avoided here.
 */

const REVISION_MAX_LENGTH = 10;

export interface PartDataRow {
  readonly index: number;
  /** Shown as a read-only label, not editable — identifies the row. */
  readonly partName: string;
  itemName: string;
  revision: string;
  assembly: string;
}

export interface EditPartDataResult {
  readonly rows: readonly PartDataRow[];
  readonly namePrefix: string;
  readonly nameSuffix: string;
}

export class EditPartDataDialog {
  private readonly overlay: HTMLElement;
  private readonly card: HTMLElement;
  private readonly body: HTMLTableSectionElement;
  private readonly saveButton: HTMLButtonElement;
  private readonly bulkRevisionInput: HTMLInputElement;
  private readonly bulkAssemblyInput: HTMLInputElement;
  private readonly namePrefixInput: HTMLInputElement;
  private readonly nameSuffixInput: HTMLInputElement;

  private resolve: ((result: EditPartDataResult | null) => void) | null = null;
  private previouslyFocused: HTMLElement | null = null;
  private rows: PartDataRow[] = [];

  constructor(private readonly root: Document = document) {
    this.overlay = root.createElement("div");
    this.overlay.className = "editpart-overlay";
    this.overlay.hidden = true;
    this.overlay.innerHTML = `
      <div class="editpart" role="dialog" aria-modal="true" aria-labelledby="editpart-title">
        <button type="button" class="editpart__close" aria-label="Close">&times;</button>
        <h2 class="editpart__title" id="editpart-title">Edit part data</h2>
        <div class="editpart-bulk">
          <div class="editpart-bulk__group">
            <label class="editpart-bulk__field">
              <span>Revision (all parts)</span>
              <input type="text" maxlength="10" class="editpart-bulk__revision" />
            </label>
            <button type="button" class="btn editpart-bulk__apply-revision">Apply to all</button>
          </div>

          <div class="editpart-bulk__group">
            <label class="editpart-bulk__field">
              <span>Assembly (all parts)</span>
              <input type="text" class="editpart-bulk__assembly" />
            </label>
            <button type="button" class="btn editpart-bulk__apply-assembly">Apply to all</button>
          </div>
        </div>
        <div class="editpart-bulk">
          <div class="editpart-bulk__group">
            <label class="editpart-bulk__field">
              <span>Part name prefix</span>
              <input type="text" class="editpart-bulk__name-prefix" />
            </label>
          </div>

          <div class="editpart-bulk__group">
            <label class="editpart-bulk__field">
              <span>Part name suffix</span>
              <input type="text" class="editpart-bulk__name-suffix" />
            </label>
          </div>
        </div>
        <div class="editpart-table-wrap">
          <table class="editpart-table">
            <thead>
              <tr><th>Part</th><th>Item name</th><th>Revision</th><th>Assembly</th></tr>
            </thead>
            <tbody></tbody>
          </table>
        </div>
        <div class="editpart__actions">
          <button type="button" class="btn editpart__cancel">Cancel</button>
          <button type="button" class="btn btn--primary editpart__save">Save</button>
        </div>
      </div>
    `;
    root.body.append(this.overlay);

    this.card = this.overlay.querySelector(".editpart")!;
    this.body = this.overlay.querySelector("tbody")!;
    this.saveButton = this.overlay.querySelector(".editpart__save")!;
    this.bulkRevisionInput = this.overlay.querySelector(".editpart-bulk__revision")!;
    this.bulkAssemblyInput = this.overlay.querySelector(".editpart-bulk__assembly")!;
    this.namePrefixInput = this.overlay.querySelector(".editpart-bulk__name-prefix")!;
    this.nameSuffixInput = this.overlay.querySelector(".editpart-bulk__name-suffix")!;

    this.overlay.querySelector(".editpart__close")!.addEventListener("click", () => this.close(null));
    this.overlay.querySelector(".editpart__cancel")!.addEventListener("click", () => this.close(null));
    this.saveButton.addEventListener("click", () =>
      this.close({
        rows: this.rows,
        namePrefix: this.namePrefixInput.value,
        nameSuffix: this.nameSuffixInput.value,
      }),
    );

    // Bulk-set: unconditionally overwrites every row for that one field,
    // including rows already customized individually — the point is "set
    // this for everything", not a fill-blanks-only merge.
    this.overlay.querySelector(".editpart-bulk__apply-revision")!.addEventListener("click", () => {
      const value = this.bulkRevisionInput.value;
      for (const row of this.rows) row.revision = value;
      this.renderRows();
    });
    this.overlay.querySelector(".editpart-bulk__apply-assembly")!.addEventListener("click", () => {
      const value = this.bulkAssemblyInput.value;
      for (const row of this.rows) row.assembly = value;
      this.renderRows();
    });

    this.overlay.addEventListener("mousedown", (event) => {
      if (event.target === this.overlay) this.close(null);
    });
  }

  /** Open the grid pre-filled with `rows` and the nest's current name prefix/suffix. Resolves with the edited result, or `null` if dismissed. */
  open(rows: readonly PartDataRow[], namePrefix: string, nameSuffix: string): Promise<EditPartDataResult | null> {
    this.rows = rows.map((row) => ({ ...row }));
    this.bulkRevisionInput.value = "";
    this.bulkAssemblyInput.value = "";
    this.namePrefixInput.value = namePrefix;
    this.nameSuffixInput.value = nameSuffix;
    this.renderRows();

    this.previouslyFocused = this.root.activeElement as HTMLElement | null;
    this.setBackgroundInert(true);
    this.overlay.hidden = false;
    this.root.addEventListener("keydown", this.handleKeydown, true);
    this.body.querySelector("input")?.focus();

    return new Promise<EditPartDataResult | null>((resolve) => {
      this.resolve = resolve;
    });
  }

  private renderRows(): void {
    this.body.replaceChildren();
    const fragment = this.root.createDocumentFragment();

    for (const row of this.rows) {
      const tr = this.root.createElement("tr");

      const partCell = this.root.createElement("td");
      partCell.className = "editpart-table__part";
      partCell.textContent = row.partName;
      partCell.title = row.partName;

      const itemNameCell = this.fieldCell(row.itemName, (value) => (row.itemName = value));
      const revisionCell = this.fieldCell(row.revision, (value) => (row.revision = value), REVISION_MAX_LENGTH);
      const assemblyCell = this.fieldCell(row.assembly, (value) => (row.assembly = value));

      tr.append(partCell, itemNameCell, revisionCell, assemblyCell);
      fragment.append(tr);
    }

    this.body.append(fragment);
  }

  private fieldCell(value: string, onChange: (value: string) => void, maxLength?: number): HTMLTableCellElement {
    const cell = this.root.createElement("td");
    const input = this.root.createElement("input");
    input.type = "text";
    input.value = value;
    if (maxLength !== undefined) input.maxLength = maxLength;
    input.addEventListener("input", () => onChange(input.value));
    cell.append(input);
    return cell;
  }

  /** Escape and Tab, the two things a native `<dialog>` handles for free. */
  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close(null);
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(
      this.card.querySelectorAll<HTMLElement>('button, input:not([disabled]), select, a[href], [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;

    if (event.shiftKey && this.root.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && this.root.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  private setBackgroundInert(inert: boolean): void {
    for (const child of Array.from(this.root.body.children)) {
      if (child !== this.overlay) (child as HTMLElement).inert = inert;
    }
  }

  private close(result: EditPartDataResult | null): void {
    this.overlay.hidden = true;
    this.setBackgroundInert(false);
    this.root.removeEventListener("keydown", this.handleKeydown, true);
    this.previouslyFocused?.focus();
    const resolve = this.resolve;
    this.resolve = null;
    resolve?.(result);
  }
}
